import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  CircleCheck,
  CircleX,
  ClipboardList,
  Eye,
  LucideAngularModule,
  Pencil,
  Plus,
  RotateCw,
  Search,
  ShieldCheck,
  Trash2,
} from 'lucide-angular';
import { User } from '../../../core/models/domain.models';
import { StaffService } from '../services/staff.service';

interface StaffForm {
  username: string;
  fullName: string;
  email: string;
  role: string;
  rmId: string;
  active: boolean;
  password: string;
}

const STAFF_ROLE_OPTIONS = ['super_admin', 'admin', 'trader', 'accounts', 'operations', 'viewer'];

@Component({
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './staff-management.page.html',
  styleUrl: './staff-management.page.scss',
})
export class StaffManagementPage implements OnInit {
  private readonly staffService = inject(StaffService);
  private readonly router = inject(Router);

  protected readonly ClipboardListIcon = ClipboardList;
  protected readonly SearchIcon = Search;
  protected readonly PlusIcon = Plus;
  protected readonly EyeIcon = Eye;
  protected readonly PencilIcon = Pencil;
  protected readonly Trash2Icon = Trash2;
  protected readonly RotateCwIcon = RotateCw;
  protected readonly ShieldCheckIcon = ShieldCheck;
  protected readonly CircleCheckIcon = CircleCheck;
  protected readonly CircleXIcon = CircleX;

  protected readonly loading = signal(false);
  protected readonly saving = signal(false);
  protected readonly search = signal('');
  protected readonly roleFilter = signal('all');
  protected readonly rows = signal<User[]>([]);
  protected readonly pageError = signal('');
  protected readonly showAddForm = signal(false);
  protected readonly formError = signal('');
  protected readonly addStaffForm = signal<StaffForm>(this.emptyForm());
  protected readonly editingStaffId = signal<string | null>(null);
  protected readonly showProfile = signal(false);
  protected readonly selectedStaff = signal<User | null>(null);
  protected readonly showDeleteConfirm = signal(false);
  protected readonly deleteTarget = signal<User | null>(null);
  protected readonly showRegenerateConfirm = signal(false);
  protected readonly regenerateTarget = signal<User | null>(null);
  protected readonly showGeneratedPassword = signal(false);
  protected readonly generatedPassword = signal('');
  protected readonly generatedForUser = signal('');
  protected readonly copyMessage = signal('');
  protected readonly staffRoleOptions = STAFF_ROLE_OPTIONS;

  protected readonly roleOptions = computed(() =>
    ['all', ...Array.from(new Set(this.rows().map((row) => row.role.trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b))],
  );

  protected readonly rmOptions = computed(() =>
    this.rows()
      .slice()
      .sort((a, b) => a.full_name.localeCompare(b.full_name))
      .map((row) => ({ id: row.id, name: row.full_name })),
  );

  protected readonly filteredRows = computed(() => {
    const role = this.roleFilter();
    const term = this.search().trim().toLowerCase();

    return this.rows().filter((row) => {
      const matchesRole = role === 'all' || row.role === role;
      if (!term) return matchesRole;

      const rmName = this.reportingManagerName(row).toLowerCase();
      const matchesSearch =
        row.full_name.toLowerCase().includes(term) ||
        row.username.toLowerCase().includes(term) ||
        row.email.toLowerCase().includes(term) ||
        row.role.toLowerCase().includes(term) ||
        rmName.includes(term);

      return matchesRole && matchesSearch;
    });
  });

  async ngOnInit(): Promise<void> {
    await this.loadStaff();
  }

  protected async loadStaff(): Promise<void> {
    this.loading.set(true);
    this.pageError.set('');
    try {
      const rows = await firstValueFrom(this.staffService.list());
      this.rows.set(rows.sort((a, b) => a.full_name.localeCompare(b.full_name)));
    } catch {
      this.pageError.set('Failed to load staff records.');
    } finally {
      this.loading.set(false);
    }
  }

  protected onSearch(value: string): void {
    this.search.set(value);
  }

  protected onRoleChange(value: string): void {
    this.roleFilter.set(value);
  }

  protected openAddForm(): void {
    this.addStaffForm.set(this.emptyForm());
    this.editingStaffId.set(null);
    this.formError.set('');
    this.showAddForm.set(true);
  }

  protected closeAddForm(): void {
    this.showAddForm.set(false);
    this.editingStaffId.set(null);
    this.formError.set('');
  }

  protected openProfile(row: User): void {
    this.selectedStaff.set(row);
    this.showProfile.set(true);
  }

  protected closeProfile(): void {
    this.showProfile.set(false);
    this.selectedStaff.set(null);
  }

  protected openPrivileges(row: User): void {
    void this.router.navigate(['/staff', row.id, 'privileges']);
  }

  protected setFormField<K extends keyof StaffForm>(key: K, value: StaffForm[K]): void {
    this.addStaffForm.update((current) => ({ ...current, [key]: value }));
  }

  protected async saveStaff(): Promise<void> {
    const form = this.addStaffForm();
    const required = [form.username, form.fullName, form.email, form.role];
    if (!this.editingStaffId()) {
      required.push(form.password);
    }
    const hasMissingFields = required.some((value) => !String(value).trim());
    if (hasMissingFields) {
      this.formError.set('Please fill all required fields.');
      return;
    }

    this.saving.set(true);
    this.formError.set('');
    const editingId = this.editingStaffId();
    try {
      if (editingId) {
        const updated = await firstValueFrom(
          this.staffService.update(editingId, {
            full_name: form.fullName.trim(),
            email: form.email.trim(),
            role: form.role,
            rm_id: form.rmId || null,
            active: form.active,
          }),
        );
        this.rows.update((current) => current.map((row) => (row.id === updated.id ? updated : row)).sort((a, b) => a.full_name.localeCompare(b.full_name)));
        if (this.selectedStaff()?.id === updated.id) this.selectedStaff.set(updated);
      } else {
        const created = await firstValueFrom(
          this.staffService.create({
            username: form.username.trim(),
            full_name: form.fullName.trim(),
            email: form.email.trim(),
            role: form.role,
            rm_id: form.rmId || null,
            active: form.active,
            password: form.password,
          }),
        );
        this.rows.update((current) => [created, ...current].sort((a, b) => a.full_name.localeCompare(b.full_name)));
      }
      this.closeAddForm();
    } catch {
      this.formError.set(editingId ? 'Failed to update staff user.' : 'Failed to create staff user.');
    } finally {
      this.saving.set(false);
    }
  }

  protected openEditForm(row: User): void {
    this.editingStaffId.set(row.id);
    this.addStaffForm.set({
      username: row.username,
      fullName: row.full_name,
      email: row.email,
      role: row.role || 'viewer',
      rmId: row.rm_id || '',
      active: row.active,
      password: '',
    });
    this.formError.set('');
    this.showAddForm.set(true);
  }

  protected deleteStaff(row: User): void {
    this.deleteTarget.set(row);
    this.showDeleteConfirm.set(true);
  }

  protected cancelDelete(): void {
    this.showDeleteConfirm.set(false);
    this.deleteTarget.set(null);
  }

  protected async confirmDelete(): Promise<void> {
    const row = this.deleteTarget();
    if (!row) return;
    try {
      await firstValueFrom(this.staffService.delete(row.id));
      this.rows.update((current) => current.filter((item) => item.id !== row.id));
      if (this.selectedStaff()?.id === row.id) this.closeProfile();
      if (this.editingStaffId() === row.id) this.closeAddForm();
      this.cancelDelete();
    } catch {
      this.pageError.set('Failed to delete staff user.');
    }
  }

  protected requestRegeneratePassword(row: User): void {
    this.regenerateTarget.set(row);
    this.copyMessage.set('');
    this.showRegenerateConfirm.set(true);
  }

  protected cancelRegeneratePassword(): void {
    this.showRegenerateConfirm.set(false);
    this.regenerateTarget.set(null);
  }

  protected async confirmRegeneratePassword(): Promise<void> {
    const target = this.regenerateTarget();
    if (!target) return;

    const password = this.generatePassword(12);
    try {
      await firstValueFrom(this.staffService.update(target.id, { password }));
      this.generatedPassword.set(password);
      this.generatedForUser.set(target.username);
      this.copyMessage.set('');
      this.showRegenerateConfirm.set(false);
      this.regenerateTarget.set(null);
      this.showGeneratedPassword.set(true);
    } catch {
      this.pageError.set('Failed to regenerate password.');
      this.cancelRegeneratePassword();
    }
  }

  protected closeGeneratedPassword(): void {
    this.showGeneratedPassword.set(false);
  }

  protected async copyGeneratedPassword(): Promise<void> {
    const password = this.generatedPassword();
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      this.copyMessage.set('Password copied');
    } catch {
      this.copyMessage.set('Copy failed');
    }
  }

  protected reportingManagerName(row: User): string {
    if (!row.rm_id) return '-';
    return this.rows().find((item) => item.id === row.rm_id)?.full_name || row.rm_id;
  }

  protected formatRole(role: string): string {
    return (role || 'viewer').replace(/_/g, ' ').toUpperCase();
  }

  protected formatDate(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString();
  }

  private emptyForm(): StaffForm {
    return {
      username: '',
      fullName: '',
      email: '',
      role: 'viewer',
      rmId: '',
      active: true,
      password: '',
    };
  }

  private generatePassword(length: number): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789@#$%&*!?';
    let result = '';
    for (let index = 0; index < length; index += 1) {
      const randomIndex = Math.floor(Math.random() * chars.length);
      result += chars[randomIndex];
    }
    return result;
  }
}
